import AsyncSelect from 'Shared/components/AsyncSelect'
import MenuListWithFooter from 'Shared/components/MenuListWithFooter'
import Step from './Step'
import CostEstimator from './Audience/CostEstimator'
import AudienceShortDescriptor from 'CampaignBuilder/components/Steps/AudienceShortDescriptor'
import useCountTargets from 'Shared/hooks/useCountTargets'

export default function Audience({ searchesNewUrl }) {
  const act = useAct()
  const {
    id,
    adminUrl,
    search,
    searchId,
    autoRefreshSearch,
    status,
    campaignType,
    textMessageBlast,
    requiredSubscription,
    subscriptionTopic,
    settings
  } = useSel((s) => s.campaign)
  const kind = textMessageBlast?.kind === 'whatsapp' ? 'whatsapp' : campaignType
  const isConstitutional = useSel(
    (s) => s.campaign.emailBlast && s.campaign.emailBlast.isConstitutional
  )
  const { baseCost, contactCost } = useSel((s) => s.campaignBuilder)
  const availableTopics = window.ENV.AVAILABLE_SUBSCRIPTIONS[requiredSubscription] || []
  const topicName = availableTopics.find((topic) => topic.topic === subscriptionTopic)?.label
  const { name, searchCount, recalculating } = useSel((s) => s.search)
  const searchIsEditable = status === 'draft'
  const newSearchPath = `${searchesNewUrl}?returning_url=${adminUrl}&linked_campaign_id=${id}`
  const isPhoneCampaign = kind === 'phone'
  const isEmailCampaign = kind === 'email'
  const isTextMessageCampaign = campaignType.startsWith('sms_')
  const isSmsConversationsCampaign = campaignType === 'sms_conversations'
  const supportsAutoRefresh = isPhoneCampaign || isSmsConversationsCampaign
  const showEstimate = (searchCount || 0) > 0 && kind !== 'email'
  const { targetCount, possibleTargetCount, invalidCountMessage } = useCountTargets()

  // validator
  const complete = searchId !== null && search !== null

  function loadCount() {
    act.search.loadCount(searchId).then(({ data: { recalculating } }) => {
      if (recalculating) {
        setTimeout(() => {
          loadCount()
        }, 2500)
      } else {
        act.campaignBuilder.updateEstimates(searchId)
      }
    })
  }

  useEffect(() => {
    if (searchId) {
      loadCount()
    }
  }, [searchId])

  function handleSubscriptionTopicChange(e) {
    act.campaign.upsert({ subscriptionTopic: e.target.value })
  }

  const onChange = (key, value) => {
    act.campaign.updateSetting(key, value)
  }

  function finish() {
    return act.campaign.persist().then(function () {
      act.campaignBuilder.updateEstimates(searchId)
      act.campaignBuilder.closeEditor()
    })
  }

  return (
    <Step
      name="To"
      editor="audience"
      editable={true}
      complete={complete}
      canClickFinish={complete}
      action={finish}
      shortDescriptor={
        <AudienceShortDescriptor
          targetCount={targetCount}
          possibleTargetCount={possibleTargetCount}
          invalidCountMessage={invalidCountMessage}
          complete={complete}
          kind={kind}
          search={search}
          count={searchCount}
          recalculating={recalculating}
          baseCost={baseCost}
          contactCost={contactCost}
          topicName={topicName}
          key={searchId}
        />
      }
    >
      <div className="fields">
        <div className="row">
          <div className="column two-thirds">
            <div className="field">
              <label>Audience</label>
              {searchIsEditable && (
                <AsyncSelect
                  entity="search"
                  value={searchId}
                  components={{ MenuList: MenuListWithFooter }}
                  redirectUrl={newSearchPath}
                  queryParameters={{
                    activeOnly: true,
                    excludeTemplates: true,
                  }}
                  onChange={(v) => act.campaign.upsert({ searchId: v })}
                />
              )}
              {!searchIsEditable && (
                <div>
                  <p className="bold">{name}</p>
                  <p className="dark-grey">
                    {supportsAutoRefresh
                      ? "You can't change this audience because this campaign is live, but you can still toggle auto-refresh below."
                      : "You can't change this audience because this campaign is live."}
                  </p>
                </div>
              )}
            </div>
            {
              availableTopics.length > 1 && <div className="field">
                <label>{window.ENV.CLIENT_NAME} subscription category</label>
                <select value={subscriptionTopic} onChange={handleSubscriptionTopicChange} disabled={!searchIsEditable}>
                  {availableTopics.map((topic) => (
                    <option key={topic.topic} value={topic.topic}>
                      {topic.label}
                    </option>
                  ))}
                </select>
              </div>
            }
            {showEstimate && (
              <CostEstimator
                baseCost={baseCost}
                contactCost={contactCost}
                possibleTargetCount={possibleTargetCount}
                targetCount={targetCount}
                kind={kind}
              />
            )}
            {isEmailCampaign && <div className="field margin-top half">
              <label className="align-center">
                <input
                  type="checkbox"
                  name="is_constitutional"
                  value="true"
                  checked={isConstitutional}
                  disabled={!searchIsEditable}
                  onClick={(e) => {
                    act.campaign.upsertEmailBlast({
                      isConstitutional: e.target.checked,
                    })
                  }}
                />
                <p className="no-margin-top no-margin-bottom margin-left">
                  Send to everyone in this audience, even if they're unsubscribed
                </p>
              </label>
              <p className="smaller dark-grey bold no-margin-top">
                If you need to contact all your members about constitutional issues.
              </p>
            </div>}
            {(isEmailCampaign || isTextMessageCampaign) && <div className="tip margin-top">
              <div className="icon">
                <SvgIconsTip />
              </div>
              <p>To send to a limited number of people in the audience, choose this in settings</p>
            </div>}
            {supportsAutoRefresh && (
              <div className="field margin-top half">
                <label>When should this audience update?</label>
                <select value={autoRefreshSearch ? 'true' : 'false'} onChange={(e) => act.campaign.upsert({ autoRefreshSearch: e.target.value === 'true' })}>
                  <option value="true">Update the audience every hour</option>
                  <option value="false">Only update the audience if I choose to</option>
                </select>
                <small>When the audience updates it pulls in new people who match the audience, and removes people who no longer match</small>
                {isSmsConversationsCampaign && (
                  <p className="smaller dark-grey bold no-margin-top margin-top half">
                    New people who enter the audience will automatically be sent the initial message. This will incur charges per recipient.
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="column third">
            <div className="tip">
              <div className="icon">
                <SvgIconsTip />
              </div>
              {isTextMessageCampaign && (
                <p>
                  More specific audience generally tend to engage more, so try only messaging people most likely to find this message useful.
                </p>
              )}
              {isEmailCampaign && (
                <p>
                  More specific audiences tend to engage better with email campaigns, so try only emailing people most likely to find the email useful.
                </p>
              )}
              {isPhoneCampaign && (
                <p>
                  More specific audiences tend to engage better with calling campaigns. This way you can ensure your script is tailored to their interests.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Step>
  )
}
